/**
 * Query Mapping Layer
 * Translates Chart Builder selections into API call configs and normalizes responses.
 */

import { doGet, doPost } from 'system/httpHelper';
import METRICS from 'utils/enums/metrics';

export class MappingError extends Error {
  constructor(message, code = 'MAPPING_ERROR', meta = {}) {
    super(message);
    this.name = 'MappingError';
    this.code = code;
    this.meta = meta;
  }
}

export const validateSelection = (sel) => {
  const errors = [];
  if (!sel) errors.push('Selection is required.');
  if (!sel.chartType) errors.push('chartType is required.');
  if (!sel.dateRange || !sel.dateRange.from || !sel.dateRange.to) {
    errors.push('dateRange.from and dateRange.to are required.');
  }
  if (!sel.interval) errors.push('interval is required.');

  const metricDef = sel?.metric ? METRICS[sel.metric] : null;

  if (sel.metric && metricDef?.family === 'device-telemetry') {
    if (!sel.siteId) errors.push('siteId is required for device telemetry.');
    if (!sel.deviceType) errors.push('deviceType is required for device telemetry.');
  }

  if (sel.metric && ['energy', 'ess', 'facility', 'vehicle-fleet'].includes(metricDef?.family)) {
    if (!sel.reportType) errors.push('reportType is required for report metrics.');
  }

  return { ok: errors.length === 0, errors };
};

export const mapSelectionToApi = (selection) => {
  const { ok, errors } = validateSelection(selection);
  if (!ok) throw new MappingError('Invalid selection', 'VALIDATION_ERROR', { errors });

  const metricDef = selection.metric ? METRICS[selection.metric] : null;
  if (!metricDef || !metricDef.buildRequest || !metricDef.normalizer) {
    throw new MappingError(
      `Metric registry incomplete or unknown for "${selection.metric}".`,
      'UNMAPPED',
    );
  }

  const call = metricDef.buildRequest(selection);
  const callsArray = Array.isArray(call) ? call : [call];

  return callsArray.map((c, idx) => ({
    key: `${selection.metric}:${idx}`,
    normalizer: metricDef.normalizer,
    ...c,
  }));
};

export const executeMappedCalls = async (calls) => {
  const results = await Promise.all(
    calls.map(async (c) => {
      const { url, method, body, params, normalizer } = c;
      let response;

      if (method === 'POST') {
        response = await doPost(url, body || {});
      } else {
        let finalUrl = url;
        if (params && Object.keys(params).length) {
          const qs = new URLSearchParams(params).toString();
          finalUrl = `${url}?${qs}`;
        }
        response = await doGet(finalUrl);
      }
      return normalizer(response, c);
    }),
  );

  if (!results.length) return { chartType: 'lines', data: [] };
  if (results.length === 1) return results[0];

  const allSameType = results.every((r) => r.chartType === results[0].chartType);
  if (!allSameType) return results;

  return {
    chartType: results[0].chartType,
    data: results.flatMap((r) => r.data || []),
  };
};
